
import React, { useEffect, useState } from 'react';
import { format, parseISO, isFuture, isToday, addMinutes } from 'date-fns';
import { Bell, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { enhancedToast } from '@/utils/notifications/enhancedToast';
import { useTimetableData } from '../../hooks/useTimetableData';
import { defaultToastConfig } from '@/utils/toastConfig';

const UpcomingClasses = ({ timetableData = [] }) => {
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const { refreshData } = useTimetableData();
  const [refreshing, setRefreshing] = useState(false);
  const [nextAlertTime, setNextAlertTime] = useState(null);

  const updateUpcomingClasses = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      setUpcomingClasses([]);
      return;
    }

    // Get all upcoming classes for today
    const now = new Date();
    const classes = [];

    data.forEach(teacher => {
      if (!teacher.classes || !Array.isArray(teacher.classes)) return;
      
      teacher.classes.forEach(classItem => {
        if (!classItem || !classItem.startTime) return;
        
        try {
          const classTime = parseISO(classItem.startTime);
          
          // Include classes that are in the future and for today
          if (isFuture(classTime) && isToday(classTime)) {
            classes.push({
              ...classItem,
              teacherName: teacher.name,
              teacherId: teacher.id
            });
          }
        } catch (error) {
          console.error('Error parsing date:', error, classItem);
        }
      });
    });

    // Sort by start time
    const sorted = classes.sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    // Take the next 5 classes
    setUpcomingClasses(sorted.slice(0, 5));
    
    // Calculate next alert time
    if (sorted.length > 0) {
      const nextClass = sorted[0];
      const classTime = parseISO(nextClass.startTime);
      const alertTime = addMinutes(classTime, -3); // 3 minutes before class
      
      if (alertTime > now) {
        setNextAlertTime(alertTime);
      }
    } else {
      setNextAlertTime(null);
    }
  };

  useEffect(() => {
    // Update upcoming classes whenever timetable data changes
    updateUpcomingClasses(timetableData);
    
    // Set up interval to refresh every minute for real-time updates
    const intervalId = setInterval(() => {
      updateUpcomingClasses(timetableData);
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, [timetableData]);
  
  // Effect for handling alerts
  useEffect(() => {
    if (!nextAlertTime) return;
    
    const now = new Date();
    const msUntilAlert = nextAlertTime.getTime() - now.getTime();
    
    if (msUntilAlert <= 0) return;
    
    // Set timeout for the upcoming class alert
    const timeoutId = setTimeout(() => {
      // Find the class that should be alerted now
      const now = new Date();
      const classToAlert = upcomingClasses.find(classItem => {
        const classTime = parseISO(classItem.startTime);
        const alertTime = addMinutes(classTime, -3);
        return Math.abs(alertTime.getTime() - now.getTime()) < 60000; // Within a minute
      });
      
      if (classToAlert) {
        enhancedToast.warning(
          `${classToAlert.teacherName} has a ${classToAlert.subject} class in 3 minutes in Room ${classToAlert.room}`,
          "Upcoming Class Alert"
        );
      }
      
      // Update the next alert time
      updateUpcomingClasses(timetableData);
    }, msUntilAlert);
    
    return () => clearTimeout(timeoutId);
  }, [nextAlertTime, upcomingClasses, timetableData]);
  
  const handleRefresh = async () => {
    // Prevent multiple clicks by checking if already refreshing
    if (refreshing) return;
    
    try {
      setRefreshing(true);
      await refreshData();
      enhancedToast.success('Upcoming classes updated');
    } catch (error) {
      // Only show error if it's not the debounce error
      if (error instanceof Error && !error.message.includes('Please wait before refreshing again')) {
        enhancedToast.error('Failed to update upcoming classes');
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center">
          <Bell className="mr-2 h-5 w-5" />
          Upcoming Classes
        </CardTitle>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleRefresh} 
          disabled={refreshing}
          className="h-8 w-8"
          title="Refresh upcoming classes"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {upcomingClasses.length > 0 ? (
          <div className="space-y-3">
            {upcomingClasses.map((classItem, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <Separator />}
                <div className="space-y-1">
                  <div className="flex justify-between items-start">
                    <div className="font-medium">{classItem.subject}</div>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        classItem.status === 'absent' ? 'border-red-500 text-red-800' : 
                        classItem.status === 'late' ? 'border-orange-500 text-orange-800' : 
                        'border-green-500 text-green-800'
                      }`}
                    >
                      {classItem.status.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">{classItem.teacherName}</div>
                  <div className="text-xs flex items-center text-muted-foreground">
                    <Clock className="h-3 w-3 mr-1" />
                    {format(parseISO(classItem.startTime), "h:mm a")} • Room {classItem.room}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <p>No upcoming classes for today</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingClasses;
